'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { Transition } from '@headlessui/react';
import { XMarkIcon, UsersIcon } from '@heroicons/react/20/solid';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface InvitationToastProps {
  id: string;
  organizationName: string;
  organizationLogo?: string | null;
  roles: string;
  onAccept: () => Promise<void>;
  onDecline: () => void;
  onClose: () => void;
}

export default function InvitationToast({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  id: _id, // Required by react-hot-toast interface, unused in component
  organizationName,
  organizationLogo,
  roles,
  onAccept,
  onDecline,
  onClose,
}: InvitationToastProps) {
  const [show, setShow] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const { darkMode } = useDarkMode();

  const getRoleLabels = (roleString: string): string[] => {
    const roleLabels: Record<string, string> = {
      admin: 'Admin',
      dashboard: 'Dashboard',
      analytics: 'Reports',
      cfo: 'Sales',
      transfer_manager: 'Transfer',
      marketing_manager: 'Marketing',
      sports_director: 'Brand',
      user_management: 'Users',
      finance_manager: 'Billing',
      settings: 'Settings',
      plugins: 'Plugins',
      log: 'Audit Log',
      api: 'API',
      approver: 'Approver',
    };

    // Parse roles - handle both JSON array and comma-separated string
    let roleArray: string[] = [];
    try {
      // Try parsing as JSON array first
      roleArray = JSON.parse(roleString);
    } catch {
      // Fallback to comma-separated string
      roleArray = roleString
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
    }

    return roleArray.map((role) => roleLabels[role.trim()] || role.trim());
  };

  const handleClose = useCallback(() => {
    setShow(false);
    setTimeout(onClose, 200); // Wait for animation to finish
  }, [onClose]);

  // Auto-hide after 30 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, 30000);

    return () => clearTimeout(timer);
  }, [handleClose]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await onAccept();
      handleClose();
    } catch {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    onDecline();
    handleClose();
  };

  return (
    <Transition show={show}>
      <div
        className={`pointer-events-auto w-full max-w-sm rounded-lg shadow-lg ring-1 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0 ${
          darkMode ? 'bg-gray-800 ring-gray-700' : 'bg-white ring-black/5'
        }`}
      >
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0 pt-0.5">
              {organizationLogo ? (
                <Image
                  alt={organizationName}
                  src={organizationLogo}
                  width={40}
                  height={40}
                  className="size-10 rounded-full object-cover"
                />
              ) : (
                <div
                  className={`flex size-10 items-center justify-center rounded-full ${
                    darkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'
                  }`}
                >
                  <UsersIcon
                    className={`size-6 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}
                  />
                </div>
              )}
            </div>
            <div className="ml-3 w-0 flex-1">
              <p className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Invitation to join {organizationName}
              </p>
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Roles: {getRoleLabels(roles).join(', ')}
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {accepting ? 'Accepting...' : 'Accept'}
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={accepting}
                  className={`inline-flex items-center rounded-md px-2.5 py-1.5 text-sm font-semibold shadow-xs ring-1 ring-inset disabled:cursor-not-allowed disabled:opacity-50 ${
                    darkMode
                      ? 'bg-gray-700 text-gray-100 ring-gray-600 hover:bg-gray-600'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Decline
                </button>
              </div>
            </div>
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={handleClose}
                className={`inline-flex rounded-md focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden ${
                  darkMode
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
}
